import React from 'react'
import styled from 'styled-components'
import * as yup from 'yup'
import Form from 'react-formal'
import { rem, borderRadius } from 'polished'
import { Heading, Text } from 'rebass'
import { Flex, Box } from 'rebass'
import { request } from 'graphql-request'
import typography from '../../utils/typography'
import {
  SmallToMediumScreenOnly,
  LargeScreenUp,
  media,
  Absolute
} from '../../utils/style'
import ContentBar from '../ContentBar/index'
import IconFormArrowRight from '../../vectors/IconFormArrowRight'
import { trackEvent } from '../../../lib/tracker'

const modelSchema = yup.object({
  email: yup
    .string()
    .default('')
    .required('Please enter an email address')
    .email('Please check your email address is correct')
    .max(255, 'Your email is over 255 characters')
})

const Subscribe = styled(Flex)`
  text-align: center;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.theme.colors.blue};
`

const SubscribeHeading = styled(Heading)`
  color: white;
  letter-spacing: 0.01em;
`

/*************************
 STYLES: Form
 **************************/
const SubscribeFieldset = styled.fieldset`
  border: none;
  position: relative;
  z-index: 0;
`

const SubscribeInput = styled(Form.Field)`
  height: ${typography.rhythm(1.4)};
  width: 100%;
  background: #addff7;
  position: relative;
  border: none;
  outline: none;
  color: ${(props) => props.theme.colors.grey};
  font-size: ${rem(15)};
  font-family: ${(props) => props.theme.font};
  padding-left: 25px;
  padding-right: 15px;
  transition: background 0.8s ${(props) => props.theme.ease.ease_out_expo};
  z-index: 2;
  border-radius: 3px;
  ${media.LargeScreenUp`
    border-radius: ${(props) => rem(props.theme.radius)}}px;
  `};
  &::placeholder {
    color: ${(props) => props.theme.colors.grey_op80};
    transition: color 0.3s ${(props) => props.theme.ease.ease_out_expo};
  }

  &:focus {
    outline: none;
    background: white;
    font-weight: 600;
    color: ${(props) => props.theme.colors.blue};
    &::placeholder {
      font-weight: 400;
    }
  }

  /* we can't style :after on inputs so to be able to have an active shadow on :focus we're forced to have a shadow div */
  &:focus:hover + .shadow,
  &:focus + .shadow {
    opacity: 1;
    transform: scale(1.08, 1.1);
  }

  &:hover + .shadow {
    opacity: 1;
    transform: scaleY(0.9);
  }

  &:hover {
    &::placeholder {
      color: ${(props) => props.theme.colors.grey};
    }
  }
`

const SubscribeInputShadow = styled(Absolute)`
  opacity: 0;
  display: block;
  top: 2px;
  left: 5%;
  width: 90%;
  height: 50%;
  background: rgba(102, 102, 102, 0.3);
  border-radius: 5px;
  z-index: 1;
  box-shadow: 0 42px 42px 2px rgba(102, 102, 102, 0.3),
    0 20px 20px 0 rgba(0, 0, 0, 0.1);
  transform: scaleY(0.5);
  transition: opacity 0.8s ${(props) => props.theme.ease.ease_out_expo},
    transform 0.4s ${(props) => props.theme.ease.ease_out_expo};
`

const SignUpButton = styled(Form.Button)`
  cursor: pointer;
  font-family: ${(props) => props.theme.font};
  font-weight: 600;
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
  color: white;
  border: none;
  outline: none;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;
  ${borderRadius('left', '0px')}
  height: ${typography.rhythm(1.4)};
  padding: 0 14px;
  ${media.LargeScreenUp`
    padding: 0 8%;
  `};
  background-color: ${(props) => props.theme.colors.blue_classic};
  transition: background-color 0.3s ${(props) => props.theme.ease.ease_out_expo};
  &:hover {
    background-color: ${(props) => props.theme.colors.blue_classic_op70};
  }

  > svg {
    height: 32px;
    width: 28px;
    margin: 10px;
  }
`

/*************************
 STYLES: Feedback
 **************************/
const SubscribeMessage = styled(Form.Message)`
  color: white;
  font-weight: 600;
  position: absolute;
  bottom: -${typography.rhythm(0.25)};
  left: 0;
  transform: translateY(100%);
`

const ErrorText = styled(Text)`
  color: white;
  font-weight: 600;
  position: absolute;
  bottom: -${typography.rhythm(0.25)};
  left: 0;
  transform: translateY(100%);
`

const submitForm = (email) => {
  trackEvent('FooterSubscribe', 'Form', 'Submit')
  const mutation = `mutation {
    signupNewsletter(email: "${email}", sourceUrl: "${document.location.toString()}") {
      email
      sourceUrl
      createdDate
    }
  }`
  return request(`${process.env.GATSBY_API_URL}/graphql`, mutation)
}

class FooterSubscribe extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      submitSuccess: false,
      submitError: false
    }
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  async handleSubmit(formData) {
    const response = await submitForm(formData.email)
    if (response && response.signupNewsletter) {
      this.setState({ submitSuccess: true })
    } else {
      this.setState({ submitError: true })
    }
  }

  render() {
    return (
      <Subscribe py={[90, 60, 100]}>
        <Box width={[10 / 12, 6 / 12]}>
          <SubscribeHeading
            mt={[10, 40]}
            mb={[20, 40]}
            fontSize={[3, rem(28)]}
            as="h5"
            id="label-newsletter-subscribe"
          >
            Subscribe to the Citygate newsletter
          </SubscribeHeading>
          <Form
            schema={modelSchema}
            onSubmit={this.handleSubmit}
            className="f24form"
            data-f24name="footer-subscribe-form"
          >
            {this.state.submitSuccess ? (
              <ContentBar bg="green" rounded>
                <strong>
                  Thank you for subscribing to the Citygate Newsletter.
                </strong>
              </ContentBar>
            ) : (
              <SubscribeFieldset>
                <SubscribeInput
                  placeholder="Enter email address"
                  name="email"
                  aria-label="Enter your email to subscribe to the Citygate newsletter"
                />
                <SubscribeInputShadow className="shadow" />

                {this.state.submitError ? (
                  <ErrorText>
                    Looks like we&#x2019;ve encountered a problem. Please retry.
                  </ErrorText>
                ) : (
                  <SubscribeMessage for="email" />
                )}
                <SignUpButton type="submit" aria-label="Sign up!">
                  <LargeScreenUp>Sign Up!</LargeScreenUp>
                  <SmallToMediumScreenOnly>
                    <IconFormArrowRight />
                  </SmallToMediumScreenOnly>
                </SignUpButton>
              </SubscribeFieldset>
            )}
          </Form>
        </Box>
      </Subscribe>
    )
  }
}

export default FooterSubscribe
